import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, ArrowRight, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { HIPAABadge } from "@/components/HIPAACompliance";
import { useWaitlistStatus } from "@/hooks/useWaitlistStatus";
import { isNativeApp } from "@/lib/native";

export default function PatientLogin() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const waitlist = useWaitlistStatus();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  // If the user's previous session was a staff session (they just got
  // timed-out or route-guard-redirected), bounce them back to the staff
  // portal so they don't see the wrong login form.
  useEffect(() => {
    const lastPortal = sessionStorage.getItem("glpflo_last_portal");
    if (lastPortal === "staff") {
      sessionStorage.removeItem("glpflo_last_portal");
      navigate("/cliniclogin", { replace: true });
    }
  }, [navigate]);

  const routeByRole = (role) => {
    if (role === "admin") navigate("/admin");
    else if (role === "prescriber") navigate("/prescriber");
    else if (role === "it") navigate("/it");
    else navigate(isNativeApp() ? "/companion" : "/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.mfa_required) {
      setMfaChallenge(result.mfa_challenge);
      return;
    }
    if (result.success) { routeByRole(result.data?.role); }
    else setError(result.error);
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await verifyMfa(mfaChallenge, mfaCode);
    setLoading(false);
    if (result.success) routeByRole(result.data?.role);
    else setError(result.error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="patient-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
            <Pill className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-2xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </span>
        </Link>

        {/* Trust badge above the card so patients see it before typing PHI. */}
        <div className="flex justify-center mb-4">
          <HIPAABadge variant="pill" />
        </div>

        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
          <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {mfaChallenge ? "Two-Factor Code" : "Welcome Back"}
          </h1>
          <p className="text-slate-500 text-sm mb-6">
            {mfaChallenge ? "Enter the 6-digit code from your authenticator app" : "Sign in to your GLPFlo account"}
          </p>

          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm mb-4" data-testid="patient-login-error">
              {error}
            </div>
          )}

          {!mfaChallenge ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                  className="mt-1 rounded-xl focus:ring-purple-500" required data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                    className="rounded-xl focus:ring-purple-500 pr-10" required data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-base" data-testid="login-submit-button">
                {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="patient-mfa-form">
              <div>
                <Label htmlFor="mfaCode" className="text-sm font-medium text-slate-700">Authentication code</Label>
                <Input id="mfaCode" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="123456"
                  className="mt-1 rounded-xl focus:ring-purple-500 font-mono text-center tracking-[0.4em] text-lg" autoFocus required data-testid="patient-mfa-input" />
                <p className="text-[11px] text-slate-400 mt-2">Or enter one of your 8-character backup codes.</p>
              </div>
              <Button type="submit" disabled={loading || mfaCode.length < 6} className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-base" data-testid="patient-mfa-submit">
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <button type="button" onClick={() => { setMfaChallenge(""); setMfaCode(""); setError(""); }} className="w-full text-center text-xs text-slate-500 hover:text-purple-600" data-testid="patient-mfa-cancel">
                &larr; Sign in with a different account
              </button>
            </form>
          )}

          <div className="text-center mt-6 space-y-2">
            {waitlist.enabled ? (
              <p className="text-sm text-slate-500">
                Not accepting new patients yet.{" "}
                <Link to="/register" className="text-purple-600 hover:text-purple-700 font-medium" data-testid="login-waitlist-link">
                  Join the waitlist
                </Link>
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                Don&apos;t have an account?{" "}
                <Link to="/register" className="text-purple-600 hover:text-purple-700 font-medium" data-testid="login-register-link">Create one</Link>
              </p>
            )}
            <p className="text-sm">
              <Link to="/forgot-password" className="text-slate-400 hover:text-purple-600 transition-colors" data-testid="forgot-password-link">Forgot your password?</Link>
            </p>
          </div>
        </div>

        {/* COA lookup — public batch transparency. Surfaced here so patients
            can verify the lot number on a vial they already have without
            having to log in first. */}
        <Link
          to="/coa"
          className="mt-6 block bg-white rounded-2xl border border-emerald-200 hover:border-emerald-400 shadow-sm hover:shadow-md transition-all px-5 py-4 group"
          data-testid="patient-login-coa-link"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center shrink-0 transition-colors">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">Verify your vial</p>
              <p className="text-xs text-slate-500">Look up your batch&apos;s Certificate of Analysis — no login needed.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-emerald-600 shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>
      </div>
    </div>
  );
}
